import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import styles from './styles.module.scss';
import { useApiQuery } from '@hooks/api/useApiQuery';
import { apiWalletService } from '@api';
import CardDataSection from '@sections/xeppt_card/card_data';
import TransactionsSection from '@sections/xeppt_card/transaction_data';
import { motion } from 'framer-motion';
import { pageAnimation } from '@const/animation';
import { useNotFound } from '@hooks/helpers/useNotFound';
import { ENewNotificationType } from '@enum';
import Button from '@components/common/button';
import { routes } from '@const/routes';
import Typography from '@components/common/typography';
import { useNavigationContext } from '@hooks/context/useNavigationContext';
import { capitalizeFirstLetter, hideCardNumber, prettifyCardType } from '@utils/index';
import { useTranslation } from 'react-i18next';
import { useLocales } from '@hooks/helpers/useLocales';

const XepptCardLayout = () => {
    const { t } = useTranslation('sections', {
        keyPrefix: 'xeppt_card.card_data'
    });
    const { id } = useParams();
    const { handleNotFound } = useNotFound();
    const { handleBack } = useNavigationContext();
    const { submitLocale } = useLocales();

    const {
        data: card,
        handleRequest: refetchCard,
        isLoading
    } = useApiQuery({
        method: () => apiWalletService.getCardById(id as string),
        condition: !!id,
        deps: [id],
        onError: (error) => {
            handleNotFound({ error, isRedirectNotFound: true, isNotification: true });
        },
        notificationType: ENewNotificationType.CARD
    });

    useEffect(() => {
        if (!id) {
            handleNotFound({ error: { statusCode: 404 }, isRedirectNotFound: true });
        }
    }, []);

    return (
        <motion.div {...pageAnimation} className={styles.wrapper}>
            <div className={styles.header}>
                <Button
                    className={styles.back}
                    leftIcon="arrow_left"
                    variant="text-gray"
                    onClick={() => handleBack(routes.xeppt_cards)}>
                    {submitLocale('back')}
                </Button>
                <Typography variant="h4">
                    {capitalizeFirstLetter(prettifyCardType(card?.type))} {t('card')}{' '}
                    <span>{hideCardNumber(card?.card_number, 'full')}</span>
                </Typography>
            </div>
            <CardDataSection card={card} refetchCard={refetchCard} isCardLoading={isLoading} />
            <TransactionsSection card_id={card?.id} />
        </motion.div>
    );
};

export default XepptCardLayout;
